import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { UserRole, Permission } from '@/types';
import { hasPermission, hasAllPermissions, canAccessRoute } from '@/utils/permissions';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: UserRole[];
  requiredPermissions?: Permission[];
  requireAll?: boolean; // If true, user must have all permissions; if false, any permission
  fallbackPath?: string;
}

/**
 * Protected Route Component with RBAC
 * 
 * Usage:
 * <ProtectedRoute allowedRoles={['recruiter', 'hr_admin']}>
 *   <JobCreationPage />
 * </ProtectedRoute>
 * 
 * <ProtectedRoute requiredPermissions={[Permission.CREATE_JOB, Permission.PUBLISH_JOB]} requireAll>
 *   <PublishJobButton />
 * </ProtectedRoute>
 */
export default function ProtectedRoute({
  children,
  allowedRoles,
  requiredPermissions,
  requireAll = true,
  fallbackPath = '/access-denied',
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access
  if (allowedRoles && allowedRoles.length > 0) {
    if (!allowedRoles.includes(user.role)) {
      return <Navigate to={fallbackPath} state={{ from: location }} replace />;
    }
  }

  // Check permission-based access
  if (requiredPermissions && requiredPermissions.length > 0) {
    if (requireAll) {
      // User must have ALL specified permissions
      if (!hasAllPermissions(user.role, requiredPermissions)) {
        return <Navigate to={fallbackPath} state={{ from: location }} replace />;
      }
    } else {
      // User must have ANY of the specified permissions
      const hasAny = requiredPermissions.some(permission => 
        hasPermission(user.role, permission)
      );
      if (!hasAny) {
        return <Navigate to={fallbackPath} state={{ from: location }} replace />;
      }
    }
  }

  // Check route-based access using path matching
  if (!canAccessRoute(location.pathname, user.role)) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // All checks passed, render children
  return <>{children}</>;
}

/**
 * Hook to check permissions within components
 */
/* eslint-disable react-refresh/only-export-components */
export const usePermissions = () => {
  const { user } = useAuth();

  return {
    hasPermission: (permission: Permission) => 
      user ? hasPermission(user.role, permission) : false,
    
    hasAllPermissions: (permissions: Permission[]) =>
      user ? hasAllPermissions(user.role, permissions) : false,
    
    hasAnyPermission: (permissions: Permission[]) =>
      user ? permissions.some(p => hasPermission(user.role, p)) : false,
    
    canAccessRoute: (path: string) =>
      user ? canAccessRoute(path, user.role) : false,
  };
};

/**
 * Component to conditionally render children based on permissions
 */
interface PermissionGateProps {
  children: ReactNode;
  requiredPermission?: Permission;
  requiredPermissions?: Permission[];
  requireAll?: boolean;
  fallback?: ReactNode;
}

export function PermissionGate({
  children,
  requiredPermission,
  requiredPermissions,
  requireAll = true,
  fallback = null,
}: PermissionGateProps) {
  const { user } = useAuth();

  if (!user) {
    return <>{fallback}</>;
  }

  // Single permission check
  if (requiredPermission) {
    if (!hasPermission(user.role, requiredPermission)) {
      return <>{fallback}</>;
    }
  }

  // Multiple permissions check
  if (requiredPermissions && requiredPermissions.length > 0) {
    if (requireAll) {
      if (!hasAllPermissions(user.role, requiredPermissions)) {
        return <>{fallback}</>;
      }
    } else {
      const hasAny = requiredPermissions.some(permission => 
        hasPermission(user.role, permission)
      );
      if (!hasAny) {
        return <>{fallback}</>;
      }
    }
  }

  return <>{children}</>;
}
